// src/screens/Friends.jsx
// Freundes-Tab: Einladungscode (8 Zeichen + QR-Code) erzeugen, Code eines Freundes
// einlösen und die wichtigsten Kennzahlen aller Freunde vergleichen.
// Daten über src/lib/friends.js (Supabase-Funktionen aus scripts/create-friends.sql).
import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import QRCode from "qrcode";
import "../App.css";
import {
  attackFriend,
  createFriendCode,
  defenseTitle,
  formatFriendCode,
  friendInviteUrl,
  loadFriendDuels,
  loadFriends,
  normalizeFriendCode,
  redeemFriendCode,
  removeFriend,
} from "../lib/friends";
import { labelForLanguage } from "../data/languages";
import { levelInfo, loadTotalExp } from "../data/totalExp";
import {
  Badge,
  Button,
  Eyebrow,
  Icon,
  Note,
  Panel,
  ProgressBar,
  ScreenHeader,
  Stack,
  TextInput,
} from "../components/ui";

const REDEEM_MESSAGES = {
  ok: (name) => ({ tone: "success", text: `${name} ist jetzt dein Freund.` }),
  already: (name) => ({ tone: "info", text: `${name} ist schon in deiner Liste.` }),
  self: () => ({ tone: "error", text: "Das ist dein eigener Code." }),
  invalid: () => ({
    tone: "error",
    text: "Code unbekannt oder abgelaufen. Bitte deinen Freund um einen neuen.",
  }),
};

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("de-DE", {
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatMinutes(min) {
  if (!min) return "0 Min.";
  if (min < 60) return `${min} Min.`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h} Std. ${m} Min.` : `${h} Std.`;
}

// Rückmeldung nach „Angreifen“ (status von attack_friend)
const ATTACK_MESSAGES = {
  ok: (name, counter) => ({
    tone: "success",
    text: `${counter ? "Gegenangriff" : "Angriff"} läuft! ${name} hat 2 Stunden Zeit für die Blitzabfrage.`,
  }),
  pending: (name) => ({ tone: "info", text: `Dein Angriff auf ${name} läuft noch.` }),
  shielded: (name) => ({ tone: "info", text: `${name} hat gerade einen Schild.` }),
  busy: (name) => ({ tone: "info", text: `${name} wird gerade schon dreifach angegriffen.` }),
  daily_limit: (name) => ({ tone: "info", text: `${name} hast du heute schon angegriffen.` }),
};

// Warum man einen Freund gerade nicht angreifen kann (targets[id].status)
function attackBlockedText(target) {
  switch (target?.status) {
    case "pending":
      return "Angriff läuft";
    case "shielded":
      return (
        <>
          <Icon name="shield" /> Schild bis {formatTime(target.shield_until)}
        </>
      );
    case "busy":
      return "Wird schon angegriffen";
    case "daily_limit":
      return "Heute schon angegriffen";
    default:
      return null;
  }
}

function formatRemaining(iso) {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "abgelaufen";
  const min = Math.ceil(ms / 60000);
  if (min < 60) return `noch ${min} Min.`;
  return `noch ${Math.floor(min / 60)}:${String(min % 60).padStart(2, "0")} Std.`;
}

// Rangliste nach Lernzeit der letzten 7 Tage (fair zwischen den Tempos),
// bei Gleichstand nach Streak
function byActivity(a, b) {
  return b.minutes7d - a.minutes7d || b.streakDays - a.streakDays;
}

export default function Friends({ language, pace, onBack, initialCode, onOpenDefense }) {
  const [friends, setFriends] = useState(null); // null = lädt
  const [loadError, setLoadError] = useState(null);
  // Angriffe/Freunde-EXP (Phase 2); null = nicht verfügbar (SQL fehlt) → Angriffe ausblenden
  const [duels, setDuels] = useState(null);
  const [myTotalExp, setMyTotalExp] = useState(null); // eigene Gesamt-EXP, frisch berechnet
  const [attackBusy, setAttackBusy] = useState(null); // Freund-ID während des Angriffs
  const [attackResult, setAttackResult] = useState(null);

  const [invite, setInvite] = useState(null); // { code, expiresAt, url, qr }
  const [inviteMode, setInviteMode] = useState(null); // null = Fenster zu | "qr" | "code"
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [copied, setCopied] = useState(false);

  const [codeInput, setCodeInput] = useState(() =>
    initialCode ? formatFriendCode(initialCode) : ""
  );
  const [redeemBusy, setRedeemBusy] = useState(false);
  const [redeemResult, setRedeemResult] = useState(null);

  // Freunde und Duelle gleichzeitig laden und zusammen setzen, damit die Seite
  // nur einmal (vom Platzhalter zum Inhalt) umspringt
  const refresh = useCallback(async () => {
    const [listResult, duelsResult, totalResult] = await Promise.allSettled([
      loadFriends(),
      loadFriendDuels(),
      loadTotalExp(language || "es-de", pace || "medium"),
    ]);
    if (totalResult.status === "fulfilled") setMyTotalExp(totalResult.value);
    if (listResult.status === "fulfilled") {
      setFriends(listResult.value);
      setLoadError(null);
    } else {
      console.warn("loadFriends failed:", listResult.reason);
      setFriends([]);
      setLoadError("Freunde konnten nicht geladen werden.");
    }
    if (duelsResult.status === "fulfilled") {
      setDuels(duelsResult.value);
    } else {
      console.warn("loadFriendDuels failed:", duelsResult.reason);
      setDuels(null);
    }
  }, [language, pace]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Beide Knöpfe (QR-Code / Code) nutzen denselben Einladungscode; erzeugt wird er
  // beim ersten Klick, „Neuer Code“ ersetzt ihn
  const handleCreateCode = async (mode = inviteMode || "qr", fresh = false) => {
    setInviteMode(mode);
    setInviteError(null);
    setCopied(false);
    if ((invite && !fresh) || inviteBusy) return;
    setInviteBusy(true);
    try {
      const { code, expiresAt } = await createFriendCode();
      const url = friendInviteUrl(code);
      const qr = await QRCode.toDataURL(url, {
        margin: 1,
        width: 220,
        color: { dark: "#0B1F4D", light: "#FFFFFF" },
      });
      setInvite({ code, expiresAt, url, qr });
    } catch (e) {
      console.warn("createFriendCode failed:", e);
      setInviteError("Code konnte nicht erzeugt werden. Bitte später noch einmal.");
    } finally {
      setInviteBusy(false);
    }
  };

  const closeInvite = () => {
    setInviteMode(null);
    setInviteError(null);
  };

  // Fenster per Escape schließen
  useEffect(() => {
    if (!inviteMode) return;
    const onKey = (e) => e.key === "Escape" && closeInvite();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [inviteMode]);

  const handleShare = async () => {
    if (!invite) return;
    const text = `Lern mit mir auf fluent! Mein Freundes-Code: ${formatFriendCode(invite.code)}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "fluent!", text, url: invite.url });
        return;
      }
      await navigator.clipboard.writeText(`${text}\n${invite.url}`);
      setCopied(true);
    } catch (e) {
      // Teilen abgebrochen → nichts tun
      if (e?.name !== "AbortError") console.warn("share failed:", e);
    }
  };

  const normalizedInput = normalizeFriendCode(codeInput);

  const handleRedeem = async () => {
    if (normalizedInput.length !== 8 || redeemBusy) return;
    setRedeemBusy(true);
    setRedeemResult(null);
    try {
      const { status, displayName } = await redeemFriendCode(normalizedInput);
      const msg = (REDEEM_MESSAGES[status] || REDEEM_MESSAGES.invalid)(displayName);
      setRedeemResult(msg);
      if (status === "ok") {
        setCodeInput("");
        refresh();
      }
    } catch (e) {
      console.warn("redeemFriendCode failed:", e);
      setRedeemResult({ tone: "error", text: "Hat nicht geklappt. Bitte später noch einmal." });
    } finally {
      setRedeemBusy(false);
    }
  };

  const handleRemove = async (friend) => {
    if (!window.confirm(`${friend.name} aus deinen Freunden entfernen?`)) return;
    try {
      await removeFriend(friend.id);
      refresh();
    } catch (e) {
      console.warn("removeFriend failed:", e);
      alert("Entfernen hat nicht geklappt.");
    }
  };

  const handleAttack = async (friend) => {
    if (attackBusy) return;
    setAttackBusy(friend.id);
    setAttackResult(null);
    try {
      const { status, counter } = await attackFriend(friend.id);
      const msg = ATTACK_MESSAGES[status];
      setAttackResult(
        msg ? msg(friend.name, counter) : { tone: "error", text: "Angriff hat nicht geklappt." }
      );
      refresh();
    } catch (e) {
      console.warn("attackFriend failed:", e);
      setAttackResult({ tone: "error", text: "Angriff hat nicht geklappt. Bitte später noch einmal." });
    } finally {
      setAttackBusy(null);
    }
  };

  const me = friends?.find((f) => f.isMe) || null;
  const sorted = friends ? friends.filter((f) => !f.isMe).sort(byActivity) : [];
  const hasFriends = sorted.length > 0;

  return (
    <>
      <ScreenHeader title="Profil" />

      <Stack gap={5}>
        {/* ---------- Eigene Kennzahlen ---------- */}
        {friends === null ? (
          <FriendCardPlaceholder me />
        ) : (
          me && (
            <FriendCard
              friend={{
                ...me,
                pace: pace || me.pace,
                totalExp: myTotalExp ?? me.totalExp,
              }}
            />
          )
        )}

        {/* ---------- Offene Angriffe auf mich ---------- */}
        {duels?.incoming.map((a) => (
          <AttackBanner key={a.id} attack={a} onDefend={() => onOpenDefense?.(a)} />
        ))}

        {/* ---------- Freunde: einladen (Code erscheint als Fenster) und Rangliste ---------- */}
        <section>
          <h2 className="friends-section-title">Freunde</h2>

          {inviteMode &&
            createPortal(
              <div className="friends-modal" onClick={closeInvite}>
                <div
                  className="friends-modal__dialog"
                  role="dialog"
                  aria-modal="true"
                  aria-label={inviteMode === "qr" ? "QR-Code teilen" : "Einladungscode"}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className="friends-modal__close"
                    onClick={closeInvite}
                    aria-label="Schließen"
                    title="Schließen"
                    autoFocus
                  >
                    {"\u2715"}
                  </button>

                  <div className="friends-invite">
                    <div className="friends-modal__title">
                      {inviteMode === "qr" ? "QR-Code teilen" : "Dein Einladungscode"}
                    </div>

                    {!invite && inviteBusy && <Note>Erzeuge Code …</Note>}
                    {inviteError && <Note tone="error">{inviteError}</Note>}

                    {invite && (
                      <>
                        {inviteMode === "qr" && (
                          <img
                            className="friends-invite__qr"
                            src={invite.qr}
                            alt={`QR-Code für den Einladungscode ${formatFriendCode(invite.code)}`}
                          />
                        )}
                        <div
                          className={`friends-invite__code${
                            inviteMode === "qr" ? " friends-invite__code--small" : ""
                          }`}
                        >
                          {formatFriendCode(invite.code)}
                        </div>
                        <div className="friends-invite__hint">
                          {inviteMode === "qr"
                            ? "Mit der Handykamera scannen lassen."
                            : "Diesen Code gibt dein Freund bei „Code eingeben“ ein."}{" "}
                          Gültig bis {formatTime(invite.expiresAt)}.
                        </div>
                        <Stack gap={2} row>
                          <Button size="sm" variant="secondary" onClick={handleShare}>
                            {copied ? (
                              <>
                                Kopiert <Icon name="check" />
                              </>
                            ) : (
                              "Teilen"
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleCreateCode(inviteMode, true)}
                            disabled={inviteBusy}
                          >
                            Neuer Code
                          </Button>
                        </Stack>
                      </>
                    )}
                  </div>
                </div>
              </div>,
              document.body
            )}

          {(friends === null || hasFriends) && <Eyebrow>Rangliste · letzte 7 Tage</Eyebrow>}
          <Panel variant="muted" className="friends-box">
            {friends === null && <FriendCardPlaceholder />}

            {friends !== null && hasFriends && (
              <Stack gap={2}>
                {sorted.map((f, i) => (
                  <FriendCard
                    key={f.id}
                    friend={f}
                    rank={i + 1}
                    onRemove={() => handleRemove(f)}
                    target={duels ? duels.targets[f.id] : null}
                    attackBusy={attackBusy === f.id}
                    onAttack={() => handleAttack(f)}
                  />
                ))}
              </Stack>
            )}
            {attackResult && hasFriends && (
              <Note tone={attackResult.tone}>{attackResult.text}</Note>
            )}

            {/* Auch bei Ladefehlern: leerer Zustand mit Plus, Fehler nur klein darunter */}
            {friends !== null && !hasFriends && (
              <div className="friends-empty">
                <div className="friends-empty__text">Du hast noch keine Freunde</div>
                <button
                  type="button"
                  className="friends-empty__add"
                  onClick={() => handleCreateCode("qr")}
                  aria-label="Freund einladen"
                  title="Freund einladen"
                >
                  +
                </button>
                {loadError && <Note tone="error">{loadError}</Note>}
              </div>
            )}
          </Panel>

          <Stack gap={2} row className="friends-invite-buttons">
            <Button variant="secondary" onClick={() => handleCreateCode("qr")}>
              QR-Code teilen
            </Button>
            <Button variant="secondary" onClick={() => handleCreateCode("code")}>
              Code erzeugen
            </Button>
          </Stack>
        </section>

        {/* ---------- Code einlösen ---------- */}
        <div>
          <label className="ui-label" htmlFor="friend-code">
            Code eingeben
          </label>
          <div className="friends-redeem">
            <TextInput
              id="friend-code"
              value={codeInput}
              onChange={(e) => {
                setCodeInput(formatFriendCode(e.target.value));
                setRedeemResult(null);
              }}
              onKeyDown={(e) => e.key === "Enter" && handleRedeem()}
              placeholder="ABCD-EFGH"
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              maxLength={9}
              className="friends-code-input"
            />
            <Button onClick={handleRedeem} disabled={normalizedInput.length !== 8 || redeemBusy}>
              {redeemBusy ? "…" : "Hinzufügen"}
            </Button>
          </div>
          {redeemResult && <Note tone={redeemResult.tone}>{redeemResult.text}</Note>}
        </div>

        <Button block variant="secondary" onClick={onBack}>
          Zurück zum Hauptmenü
        </Button>
      </Stack>
    </>
  );
}

/** Hinweis auf einen offenen Angriff mit Knopf zur Blitzabfrage (auch im Hauptmenü) */
export function AttackBanner({ attack, onDefend }) {
  // Restzeit jede Minute neu anzeigen
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="attack-banner" role="alert">
      <span className="attack-banner__icon">
        <Icon name="bolt" />
      </span>
      <span className="attack-banner__text">
        <strong>
          {attack.attackerName} {attack.isCounter ? "schlägt zurück" : "greift dich an"}!
        </strong>
        <span className="attack-banner__time">
          {attack.started ? "Abwehr läuft" : `Blitzabfrage ${formatRemaining(attack.expiresAt)}`}
        </span>
      </span>
      <Button size="sm" onClick={onDefend} disabled={attack.started}>
        Abwehren
      </Button>
    </div>
  );
}

function formatExp(value) {
  return `${Math.round(value || 0).toLocaleString("de-DE")} EXP`;
}

/** Eigene Gesamt-EXP mit den Sprachniveaus als Meilensteinen */
function LevelBar({ total, levels }) {
  const ratio = levels.max > 0 ? Math.min(1, total / levels.max) : 0;
  const hint = levels.next
    ? `${levels.reached ? `Niveau ${levels.reached} erreicht · ` : ""}noch ${formatExp(
        levels.next.at - total
      )} bis ${levels.next.label}`
    : "Kurs geschafft – Niveau B2!";

  return (
    <div className="level-bar">
      <div className="ui-progress__label">
        <span>Gesamt-EXP</span>
        <span>
          {formatExp(total)}
          {levels.reached && (
            <Badge tone="sun" className="level-bar__badge">
              {levels.reached}
            </Badge>
          )}
        </span>
      </div>
      <div className="level-bar__track">
        <ProgressBar value={ratio} tone="exp" />
        {levels.milestones.map((m) => (
          <span
            key={m.label}
            className={`level-bar__tick${total >= m.at ? " level-bar__tick--done" : ""}`}
            style={{ left: `${(m.at / levels.max) * 100}%` }}
          />
        ))}
      </div>
      <div className="level-bar__labels">
        {levels.milestones.map((m) => (
          <span
            key={m.label}
            className={`level-bar__label${total >= m.at ? " level-bar__label--done" : ""}`}
            style={{ left: `${(m.at / levels.max) * 100}%` }}
          >
            {m.label}
          </span>
        ))}
      </div>
      <div className="level-bar__hint">{hint}</div>
    </div>
  );
}

/** Grauer Platzhalter in Größe einer Freundeskarte, solange geladen wird */
function FriendCardPlaceholder({ me = false }) {
  return (
    <Panel
      raised={me}
      className={`friend-card friend-card--placeholder${me ? " friend-card--me" : ""}`}
      aria-hidden="true"
    >
      <div className="friend-card__head">
        <span className="friend-skeleton" style={{ width: "45%" }} />
        <span className="friend-skeleton friend-skeleton--pill" />
      </div>
      <span className="friend-skeleton friend-skeleton--bar" />
      {/* eigene Karte hat zusätzlich Niveaus und Hinweis unter dem Balken */}
      {me && <span className="friend-skeleton friend-skeleton--level" />}
      <div className="friend-card__stats friend-card__stats--placeholder">
        <span className="friend-skeleton" style={{ width: "75%" }} />
        <span className="friend-skeleton" style={{ width: "30%" }} />
      </div>
    </Panel>
  );
}

function FriendCard({ friend, rank, onRemove, target, attackBusy, onAttack }) {
  const hasStats = friend.currentWeek != null;
  const title = defenseTitle(friend.defenseStreak);
  const blocked = attackBlockedText(target);
  const expRatio =
    friend.weekExpMax > 0 ? Math.min(1, (friend.weekExp || 0) / friend.weekExpMax) : 0;
  // Gesamt-EXP bis zum Kursende; fehlt der Wert (alte App-Version, SQL fehlt), Wochen-EXP
  const hasTotal = friend.totalExp != null;
  const levels = hasTotal ? levelInfo(friend.totalExp, friend.pace || "medium") : null;

  return (
    <Panel raised={friend.isMe} className={`friend-card${friend.isMe ? " friend-card--me" : ""}`}>
      <div className="friend-card__head">
        {rank != null && <span className="friend-card__rank">{rank}</span>}
        <span className="friend-card__name">
          {friend.name}
        </span>
        {hasStats && <Badge tone="blue">Woche {friend.currentWeek}</Badge>}
        {friend.streakDays > 0 && (
          <Badge tone="sun" title="Tage in Folge geübt">
            <Icon name="flame" /> {friend.streakDays}
          </Badge>
        )}
        {friend.shieldUntil && (
          <Badge tone="mint" title={`Schild bis ${formatTime(friend.shieldUntil)}`}>
            <Icon name="shield" label="Schild aktiv" />
          </Badge>
        )}
      </div>

      {hasTotal && friend.isMe ? (
        <LevelBar total={friend.totalExp} levels={levels} />
      ) : hasTotal ? (
        <ProgressBar
          className="friend-card__exp"
          value={levels.max > 0 ? friend.totalExp / levels.max : 0}
          tone="exp"
          label="Gesamt-EXP"
          valueLabel={formatExp(friend.totalExp)}
        />
      ) : hasStats ? (
        <ProgressBar
          className="friend-card__exp"
          value={expRatio}
          tone="exp"
          label="Wochen-EXP"
          valueLabel={`${friend.weekExp ?? 0} / ${friend.weekExpMax ?? 0}`}
        />
      ) : (
        <div className="friend-card__empty">Noch keine Kennzahlen</div>
      )}

      <div className="friend-card__stats">
        <span>
          <strong>{friend.masteredWords ?? 0}</strong> Wörter gemeistert
        </span>
        <span>
          <strong>{formatMinutes(friend.minutes7d)}</strong> gelernt
        </span>
        <span>
          <strong>{friend.friendExp}</strong> Freunde-EXP
        </span>
        {title && (
          <span>
            <Icon name="medal" /> <strong>{title}</strong> ({friend.defenseStreak} Abwehren in Folge)
          </span>
        )}
        {friend.languageId && <span>{labelForLanguage(friend.languageId)}</span>}
      </div>

      {(onRemove || target) && (
        <div className="friend-card__actions">
          {onRemove ? (
            <button type="button" className="friend-card__remove" onClick={onRemove}>
              Entfernen
            </button>
          ) : (
            <span />
          )}
          {target &&
            (target.status === "ok" ? (
              <Button size="sm" onClick={onAttack} disabled={attackBusy}>
                {attackBusy ? (
                  "…"
                ) : target.counter ? (
                  <>
                    Gegenangriff <Icon name="counter" />
                  </>
                ) : (
                  <>
                    Angreifen <Icon name="bolt" />
                  </>
                )}
              </Button>
            ) : (
              <span className="friend-card__attack-note">{blocked}</span>
            ))}
        </div>
      )}
    </Panel>
  );
}
